import "../styles/sidebar.css";
import useDealStore from "../store/dealStore";
import useOrganizationStore from "../store/organizationStore";
import useAuthStore, { isDeveloper, isAdmin } from "../store/authStore";

// Explicit sidebar order — Module 2 (Market Intelligence) is header-pinned, not listed here.
// "Underwriting Metrics" links to Module 4 (same page, sub-section indicator).
// Correct pipeline order: Intake → Underwriting (3) → Deal Score (4) → Lenders → ...
const SIDEBAR_ITEMS = [
  { key: 'qda', moduleNumber: 'quick-deal', label: 'Quick Deal Analysis', icon: '⚡', alwaysUnlocked: true, isQuickDeal: true },
  { key: 'm1', moduleNumber: 1, label: 'Deal Lead Generation', icon: '🔍' },
  { key: 'm3', moduleNumber: 3, label: 'Deal Intake & Documents', icon: '📁' },
  { key: 'm4', moduleNumber: 4, label: 'Underwriting Engine', icon: '⚙️' }, // Step 3
  { key: 'm5', moduleNumber: 5, label: 'Deal Score & Verdict', icon: '🏆' }, // Step 4
  { key: 'm6', moduleNumber: 6, label: 'Lender Match Engine', icon: '🏦' },
  { key: 'm7', moduleNumber: 7, label: 'Deal Syndication Engine', icon: '🤝' },
  { key: 'm8', moduleNumber: 8, label: 'Loan Package Generator', icon: '📧' },
  { key: 'm9', moduleNumber: 9, label: 'Outreach & Communication', icon: '' },
  { key: 'm10', moduleNumber: 10, label: 'Actions & Follow-up', icon: '📝' },
  { key: 'm11', moduleNumber: 11, label: 'LOI Generator', icon: '📜' },
  { key: 'm12', moduleNumber: 12, label: 'PSA Generator', icon: '🔐' },
  { key: 'm13', moduleNumber: 13, label: 'Escrow & Title Workflow', icon: '🔎' },
  { key: 'm14', moduleNumber: 14, label: 'Due Diligence & Title Clearance', icon: '🏠' },
  { key: 'm15', moduleNumber: 15, label: 'Financing, Closing & Recording', icon: '📊' },
  { key: 'm16', moduleNumber: 16, label: 'Asset Plan & Hold', icon: '🚀' },
  { key: 'm17', moduleNumber: 17, label: 'Exit Strategy', icon: '🏁' },
];

const DISPLAY_NUMBERS = {
  qda: '⚡',
  m1: '1',
  m3: '2',
  m4: '3',  // Underwriting = step 3
  m5: '4',  // Deal Score   = step 4
  m6: '5',
  m7: '6',
  m8: '7',
  m9: '8',
  m10: '9',
  m11: '10',
  m12: '11',
  m13: '12',
  m14: '13',
  m15: '14',
  m16: '15',
  m17: '16',
};

// Total pipeline steps (excluding sub-items and entry)
const TOTAL_STEPS = 16;

function Sidebar() {
  const { activeModule, availableModules, navigateToModule, getModuleStatus, sidebarCollapsed, toggleSidebar } = useDealStore();
  const { profile, isProfileComplete } = useOrganizationStore();
  const { user, logout } = useAuthStore();

  const isAdmin = user && ['admin', 'host'].includes(user.role);

  const handleHomeClick = () => {
    navigateToModule(0);
  };

  const handleItemClick = (item) => {
    if (item.isQuickDeal || item.moduleNumber === 'quick-deal') {
      navigateToModule('quick-deal');
      return;
    }
    if (item.alwaysUnlocked || item.moduleNumber === 'entry') {
      navigateToModule('entry');
      return;
    }
    const isAvailable = availableModules.includes(parseInt(item.moduleNumber));
    if (isAvailable) {
      navigateToModule(parseInt(item.moduleNumber));
    }
  };

  const getItemStatus = (item) => {
    if (item.isQuickDeal || item.moduleNumber === 'quick-deal') {
      return { isCurrent: activeModule === 'quick-deal', isComplete: false, isAvailable: true };
    }
    if (item.alwaysUnlocked || item.moduleNumber === 'entry') {
      return { isCurrent: activeModule === 'entry', isComplete: false, isAvailable: true };
    }
    const modNum = parseInt(item.moduleNumber);
    const isAvailable = availableModules.includes(modNum);
    const status = getModuleStatus(modNum);
    const isCurrent = activeModule === modNum;
    return { isCurrent, isComplete: status.isComplete, isAvailable };
  };

  const completedCount = SIDEBAR_ITEMS.filter(item => {
    if (item.subItem || item.alwaysUnlocked || item.moduleNumber === 'entry' || item.isQuickDeal) return false;
    const status = getModuleStatus(parseInt(item.moduleNumber));
    return status.isComplete;
  }).length;

  // ── Admin-only sidebar: clean, focused, no deal/pipeline chrome ────────────
  if (isAdmin) {
    return (
      <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <div>
          <div className="sidebar-logo">
            <img className="sidebar-logo-img" src="/assets/LOGO-new.png" alt="AcquireOS AI" />
          </div>
          <button
            className="sidebar-collapse-btn"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>

          {/* Admin badge */}
          {!sidebarCollapsed && (
            <div style={{ margin: '8px 14px 12px', padding: '8px 12px', background: 'rgba(239,68,68,0.12)', border: '1.5px solid rgba(239,68,68,0.3)', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ef4444' }}>
                  <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z" />
                  <path d="M3 20h18" />
                </svg>
              </span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#ef4444', letterSpacing: '0.08em', textTransform: 'uppercase' }}>System Administrator</div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 1 }}>{user?.email}</div>
              </div>
            </div>
          )}

          <ul className="module-list">
            <li className="module-separator"><span>Administration</span></li>
            <li
              className={`module-item ${activeModule === 'admin-dashboard' ? 'active' : ''}`}
              onClick={() => navigateToModule('admin-dashboard')}
              title="Admin Command Center"
            >
              <div className="module-indicator" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <div className="module-content">
                <span className="module-name">Admin Dashboard</span>
              </div>
            </li>
          </ul>
        </div>

        {/* Admin logout at the bottom */}
        <div className="sidebar-footer" style={{ padding: '14px' }}>
          <button
            onClick={() => { logout(); }}
            style={{
              width: '100%',
              padding: '10px 16px',
              background: 'rgba(239,68,68,0.15)',
              border: '1.5px solid rgba(239,68,68,0.4)',
              borderRadius: 8,
              color: '#ef4444',
              fontWeight: 700,
              fontSize: 13,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
    );
  }

  // ── Standard user sidebar ──────────────────────────────────────────────────
  return (
    <div className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <img className="sidebar-logo-img" src="/assets/LOGO-new.png" alt="AcquireOS AI" />
      </div>

      {/* ── Collapse toggle button ── */}
      <button
        className="sidebar-collapse-btn"
        onClick={toggleSidebar}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? '→' : '←'}
      </button>

      <ul className="module-list">
        {/* Home option */}
        <li
          className={`module-item ${activeModule === 0 ? 'active' : ''} home-item`}
          onClick={handleHomeClick}
          title="Home Dashboard"
        >
          <div className="module-indicator">🏠</div>
          <div className="module-content">
            <span className="module-name">Home</span>
          </div>
        </li>

        {/* Pre-Pipeline separator (Quick Deal Analysis) */}
        <li className="module-separator">
          <span>Pre-Pipeline</span>
        </li>

        {/* Quick Deal Analysis item — rendered first, always unlocked */}
        {(() => {
          const qdaItem = SIDEBAR_ITEMS[0];
          const { isCurrent } = getItemStatus(qdaItem);
          return (
            <li
              key={qdaItem.key}
              className={['module-item', isCurrent ? 'active' : '', 'qda-sidebar-item'].filter(Boolean).join(' ')}
              onClick={() => handleItemClick(qdaItem)}
              title="Quick Deal Analysis"
            >
              <div className="module-indicator" style={{ fontSize: '14px' }}>⚡</div>
              <div className="module-content">
                <span className="module-name">{qdaItem.label}</span>
              </div>
            </li>
          );
        })()}

        {/* Acquisition Pipeline separator */}
        <li className="module-separator">
          <span>Acquisition Pipeline</span>
        </li>

        {/* Pipeline items (skip qda which is index 0) */}
        {SIDEBAR_ITEMS.slice(1).map((item) => {
          const { isCurrent, isComplete, isAvailable } = getItemStatus(item);
          const displayNum = DISPLAY_NUMBERS[item.key];

          return (
            <li
              key={item.key}
              className={[
                'module-item',
                isCurrent ? 'active' : '',
                isComplete ? 'completed' : '',
                !isAvailable && !item.alwaysUnlocked ? 'disabled' : '',
                item.subItem ? 'sub-item' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => handleItemClick(item)}
              title={
                item.alwaysUnlocked
                  ? item.label
                  : !isAvailable
                    ? `${item.label} — complete previous steps to unlock`
                    : item.label
              }
            >
              <div className="module-indicator">
                {isComplete && !item.subItem
                  ? '✓'
                  : item.subItem
                    ? <span style={{ fontSize: '10px', opacity: 0.6 }}>↳</span>
                    : (displayNum || item.icon)}
              </div>
              <div className="module-content">
                <span className="module-name">{item.label}</span>
                {!isAvailable && !item.alwaysUnlocked && (
                  <span className="module-lock">🔒</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* CRM tab */}
      <ul className="module-list" style={{ marginTop: 4 }}>
        <li className="module-separator"><span>CRM</span></li>
        <li
          className={`module-item ${activeModule === 'crm' ? 'active' : ''}`}
          onClick={() => navigateToModule('crm')}
          title="M2M CRM"
        >
          <div className="module-indicator">📊</div>
          <div className="module-content">
            <span className="module-name">M2M Cash Flow</span>
          </div>
        </li>
      </ul>

      {/* Organization Settings */}
      <ul className="module-list" style={{ marginTop: 4 }}>
        <li className="module-separator"><span>Settings</span></li>
        <li
          className={`module-item ${activeModule === 'org-profile' ? 'active' : ''}`}
          onClick={() => navigateToModule('org-profile')}
          title="Organization Profile"
          style={{ position: 'relative' }}
        >
          <div className="module-indicator">🏢</div>
          <div className="module-content">
            <span className="module-name">Organization Profile</span>
            {!isProfileComplete && (
              <span className="module-lock" title="Setup not complete" style={{ color: '#f59e0b' }}>!</span>
            )}
          </div>
        </li>

        {isDeveloper(user) && (
          <li
            className={`module-item ${activeModule === 'dev-console' ? 'active' : ''}`}
            onClick={() => navigateToModule('dev-console')}
            title="Developer Console"
          >
            <div className="module-indicator">⚙️</div>
            <div className="module-content">
              <span className="module-name">Dev Console</span>
            </div>
          </li>
        )}
      </ul>

      <div className="sidebar-footer">
      </div>
    </div>
  );
}

export default Sidebar;
